/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import { useState, useEffect } from 'react'
import { string } from 'prop-types'
import _get from 'lodash/get'
import _isEmpty from 'lodash/isEmpty'
import loadScript from '@helpers/loadScript'
import { useSelector } from 'react-redux'
import { store } from '@redux/store'
import Cookies from 'universal-cookie'
import { identifySegmentUser, getPersonalizedSegmentData } from '@services/segment'
import { emailSignup } from '@services/email'
import { saveLocalStorage, getFromBrowserStorage, removeLocalStorage } from '@helpers/storage'
import { getFingerprintId } from '@components/integrations/Fingerprint'
import { addToDataLayer_nextgen } from '@helpers/google-tag-manager'
import { setSegmentId } from '@redux/modules/segment'

// hooks
import useWindow from '@hooks/useWindow'
import { updateAttribute } from '@components/GrowthbookComponent'
import shouldTrackUser from '../Util/shouldTrackUser'

const SEGMENT_WINDOW_PROPERTY = 'analytics'

const RTG_SEG_HAS_EMAIL = 'user_has_email'

const RTG_SEG_USER_ID = 'ajs_user_id'
const RTG_SEG_ANONYMOUS_ID = 'ajs_anonymous_id'

export const retrieveUserContext = async userId => {
  const now = new Date().getTime()
  const currentUserContext = getFromBrowserStorage('local', 'rtgUserContext')
  const hasEmail = getFromBrowserStorage('local', 'user_has_email')
  const shouldRetreiveContext = _isEmpty(currentUserContext) || currentUserContext?.expire < now
  if (userId && shouldRetreiveContext) {
    const response = await getPersonalizedSegmentData(userId)
    const newUserContext = _get(response, 'data', {})
    const tenMinutes = 60 * 10 * 1000
    newUserContext.expire = now + tenMinutes

    saveLocalStorage('rtgUserContext', newUserContext)
    updateAttribute('user_has_email', hasEmail)
    updateAttribute('rtgUserContext', newUserContext)
  }
}

export const getUserId = () => getFromBrowserStorage('local', RTG_SEG_USER_ID)
export const setUserId = (userId, traits = {}, skipIdentification = false) => {
  // const cookies = new Cookies()
  // saveLocalStorage(RTG_SEG_USER_ID, userId)
  // cookies.set(RTG_SEG_USER_ID, userId, {
  //   path: '/',
  //   secure: true,
  //   sameSite: 'lax',
  // })

  if (!skipIdentification) {
    window?.analytics?.identify?.(userId, traits)
  }
}
export const checkUserId = userId =>
  (typeof userId === 'string' && !userId.startsWith('use_') && userId !== getFingerprintId()) ||
  !userId?.length ||
  userId === null

export const getAnonymousId = () => getFromBrowserStorage('local', RTG_SEG_ANONYMOUS_ID)
export const setAnonymousId = anonymousId => {
  const cookies = new Cookies()
  saveLocalStorage(RTG_SEG_ANONYMOUS_ID, anonymousId)
  cookies.set(RTG_SEG_ANONYMOUS_ID, anonymousId, {
    path: '/',
    secure: true,
    sameSite: 'lax',
  })
}
export const checkAnonymousId = anonymousId => anonymousId === getFingerprintId()

export const setUserHasEmail = hasEmail => saveLocalStorage(RTG_SEG_HAS_EMAIL, hasEmail)

const signupUser = data =>
  emailSignup({
    anonymousId: getAnonymousId(),
    email: data?.email,
    subsourcecode: data?.subsourcecode || 'rtg-web-user',
    zipcode: data?.zipcode,
    phone: data?.phone,
    userid: 'rtg-web-user',
    sourcecode: 'ONLINE',
    sourcekey: 'ONLINE',
    storenumber: '970', // 970 is the specific store number for e-commerce
    fingerprint_id: getFingerprintId(),
    ...data,
  })

export const Segment = ({ apiKey }) => {
  const { checkProperty } = useWindow()
  const [segmentLoaded, setSegmentLoaded] = useState(false)
  const { segmentId } = useSelector(state => state.segment)
  const localUserId = getUserId()
  const fingerprintId = getFingerprintId()

  useEffect(() => {
    if (apiKey) {
      const url = `https://cdn.segment.com/analytics.js/v1/${apiKey}/analytics.min.js`
      shouldTrackUser()
        .then(trackable => {
          if (!trackable.isTrackable) {
            throw new Error(`User is not trackable: ${JSON.stringify(trackable)}`)
          }
        })
        .then(() => loadScript(url))
        .then(() => checkProperty(SEGMENT_WINDOW_PROPERTY))
        .then(() => setSegmentLoaded(true))
        .catch(err => console.log(`Loading segment failed from ${url}. ${err.message}`))
    }
  }, [apiKey])

  useEffect(() => {
    const anonymousId = getAnonymousId()
    const userId = !_isEmpty(segmentId) ? segmentId : localUserId
    const pathname = typeof window !== 'undefined' && window.location.pathname
    if (segmentLoaded && !_isEmpty(fingerprintId) && checkAnonymousId(anonymousId)) {
      addToDataLayer_nextgen('segment_loaded', {
        segment_loaded: true,
        pathname,
        segmentId: userId,
        fingerprintId,
        anonymousId,
      })
    }
  }, [segmentLoaded, fingerprintId, segmentId])

  return null
}

export const identifyUser = async ({ signup, email, subsourcecode, ...attributes } = {}) => {
  if (!checkUserId(getUserId())) {
    window?.analytics?.user?.().logout?.()
    setUserId(null, {}, true)
    setAnonymousId(getFingerprintId())
  }
  if (!checkAnonymousId(getAnonymousId())) {
    setAnonymousId(getFingerprintId())
  }
  const localUserId = getUserId()
  const fingerprintId = getFingerprintId()
  const localAnonymousId = getAnonymousId()

  // SIGNUP USER
  if (signup === 'true') {
    signupUser({ signup, email, subsourcecode, ...attributes })
  }

  const bodyRequest = {
    ...(email ? { email } : {}),
    ...(!_isEmpty(fingerprintId) ? { fingerprintId } : { anonymousId: localAnonymousId }),
    attributes,
  }

  const responseSegmentUser = await identifySegmentUser(bodyRequest)
  const { userId, anonymousId, hasEmail } = responseSegmentUser || {}

  setUserHasEmail(hasEmail)
  setUserId(userId?.length ? userId : null, { fingerprint_id: fingerprintId, email, ...attributes })
  addToDataLayer_nextgen('segment_identify', {
    userId,
    fingerprintId,
    anonymousId,
    ...attributes,
  })

  if (userId?.length || anonymousId?.length) {
    // SET STATE
    store.dispatch(setSegmentId(userId))

    // GET PERSONALIZED SEGMENT DATA
    retrieveUserContext(userId)

    return { userId, anonymousId }
  }
  return { userId: localUserId, anonymousId: localAnonymousId }
}

export const identifyFingerprint = () => {
  const fingerprintId = getFingerprintId()
  const localUserId = getUserId()
  const localAnonymousId = getAnonymousId()

  if (!checkAnonymousId(localAnonymousId) || !checkUserId(localUserId)) {
    identifyUser()
  }
  try {
    /* eslint-disable no-undef */
    FS.identify(fingerprintId)
  } catch (err) {
    if (process.env.NODE_ENV === 'production') {
      throw new Error({ err, message: 'Error identifying Fullstory user. (fingerprint_id)' })
    } else {
      console.error('Error identifying Fullstory user:', err)
    }
  }
}

Segment.propTypes = {
  apiKey: string,
}
